import { Parser } from '@json2csv/plainjs'
import fileDownload from 'js-file-download'
import type { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'

import type { SiteRevenuePerMarketProgram } from '@/features/site/types/revenue'
import { getMarketProgramLabel } from '@/features/site/utils/revenueUtils/getMarketProgramLabel'
import { isBrpMarketProgram } from '@/features/site/utils/revenueUtils/isBrpMarketProgram'
import { isEnergyMarketProgram } from '@/features/site/utils/revenueUtils/isEnergyMarketProgram'

type RevenueDateRange = {
  startDate: DateTime
  endDate: DateTime
}

export function useExportRevenue() {
  const { t } = useTranslation()

  function exportCsv(revenues: SiteRevenuePerMarketProgram | null, siteName: string, dateRange: RevenueDateRange) {
    const translatedTimestamp = t('site_revenue.export.timestamp')
    const translatedSoldCapacity = t('site_revenue.export.sold_capacity')
    const translatedActivatedEnergy = t('site_revenue.export.activated_energy')
    const translatedRevenue = t('site_revenue.export.total_revenue')
    const translatedMarketProgram = t('site_revenue.export.market_program')

    const columnLabels = [
      translatedTimestamp,
      translatedSoldCapacity,
      translatedActivatedEnergy,
      translatedRevenue,
      translatedMarketProgram,
    ]

    const csvData: Record<string, any>[] = []

    if (revenues) {
      for (const [marketProgramName, rows] of revenues.entries()) {
        const isEnergy = isEnergyMarketProgram(marketProgramName)
        // BRP billing-line programs carry neither sold capacity nor activated energy, so both columns stay blank
        const isBrp = isBrpMarketProgram(marketProgramName)
        for (const row of rows ?? []) {
          csvData.push({
            [translatedTimestamp]: row.timestamp,
            [translatedSoldCapacity]: isEnergy || isBrp ? '' : row.soldCapacity.toString(),
            [translatedActivatedEnergy]: isEnergy ? row.activatedEnergy.toString() : '',
            [translatedRevenue]: row.revenue.toString(),
            [translatedMarketProgram]: getMarketProgramLabel(t, marketProgramName),
          })
        }
      }
    }

    const parser = new Parser({
      fields: columnLabels,
    })
    const csv = parser.parse(csvData)
    const translatedRevenues = t('site_revenue.export.revenues')
    const fileName = `${translatedRevenues} - ${dateRange.startDate.toISODate()} - ${dateRange.endDate.toISODate()} - ${siteName}.csv`
    fileDownload(csv, fileName)
  }

  return exportCsv
}
